import { useGetIdentity, useLogout } from "@refinedev/core";
import { Activity, AudioLines, ClipboardCheck, BookOpen, Bot, CalendarCheck, CalendarDays, ChartColumnIncreasing, ChartLine, Coins, ChevronRight, FileChartColumn, FileSearch, Headset, Hourglass, House, Inbox, KeyRound, Link2, ListTodo, LogOut, Megaphone, MessageCircle, MessageSquareQuote, MessageSquareWarning, MessagesSquare, Moon, PackageX, PhoneCall, PhoneMissed, PhoneOutgoing, Puzzle, ScrollText, Search, Send, Shuffle, Store, TextSearch, Truck, UserRound, Video, UsersRound, Wallet } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { Link, useLocation } from "react-router";

import { Annotator } from "@/components/Annotator";
import { NavCommand } from "@/components/layout/NavCommand";
import { QuickJump } from "@/components/layout/QuickJump";
import { CHAT_GROUPS, CLIENT_CHATS, has, KNOWLEDGE_EDIT, PERMISSIONS_EDIT, QC_REMARK, REQUESTS_VIEW, VIDEO_VIEW, VOICE_LAB, type PermissionCode } from "@/lib/access";
import type { CurrentUser } from "@/lib/providers";

/**
 * Пункт меню. `to` обязателен: кнопка, за которой нет экрана, — обещание,
 * которого кабинет не исполняет.
 */
type NavItem = { label: string; icon: ReactNode; to: string; need?: PermissionCode; needAny?: PermissionCode[] };

// Пункты, повторённые в «Важном» и в своём блоке, объявлены один раз.
const HOME: NavItem = { label: "Главная", icon: <House size={14} />, to: "/" };
const SEARCH: NavItem = { label: "Поиск", icon: <Search size={14} />, to: "/search" };
const CHATS: NavItem = { label: "Чаты", icon: <Send size={14} />, to: "/chats", need: CLIENT_CHATS };
const LEADS: NavItem = { label: "Лиды", icon: <PhoneCall size={14} />, to: "/leads" };
const CALLS: NavItem = { label: "Звонки", icon: <PhoneMissed size={14} />, to: "/calls" };
const DIALING: NavItem = { label: "Обзвон", icon: <PhoneOutgoing size={14} />, to: "/dialing" };
const DEAL: NavItem = { label: "Карточка сделки", icon: <FileSearch size={14} />, to: "/deals" };
const DELIVERY: NavItem = { label: "Доставки", icon: <Truck size={14} />, to: "/delivery" };

/** Блоки меню — аккордеон: открыт один, по умолчанию «Важное» (28.10.26). */
const NAV: { group: string; items: NavItem[] }[] = [
  { group: "Важное", items: [HOME, SEARCH, CHATS, LEADS, CALLS, DIALING, DEAL, DELIVERY] },
  {
    group: "Лиды и сделки",
    items: [
      LEADS,
      { label: "Распределение", icon: <Shuffle size={14} />, to: "/distribution" },
      { label: "Записи", icon: <CalendarCheck size={14} />, to: "/appointments" },
      DEAL,
      SEARCH,
    ],
  },
  { group: "Звонки", items: [CALLS, DIALING] },
  {
    group: "Чаты",
    items: [
      CHATS,
      { label: "Чаты · Аналитика", icon: <ChartColumnIncreasing size={14} />, to: "/chat-stats", need: CLIENT_CHATS },
      { label: "Шаблоны", icon: <MessageSquareQuote size={14} />, to: "/templates", need: CLIENT_CHATS },
      { label: "Мессенджер", icon: <MessageCircle size={14} />, to: "/messenger" },
      { label: "Чаты Bitrix", icon: <MessagesSquare size={14} />, to: "/bitrix-chats" },
      { label: "Обращения", icon: <Inbox size={14} />, to: "/requests", need: REQUESTS_VIEW },
    ],
  },
  {
    group: "Знания и качество",
    items: [
      { label: "База знаний", icon: <BookOpen size={14} />, to: "/knowledge" },
      { label: "Что спрашивают", icon: <TextSearch size={14} />, to: "/keywords", need: CLIENT_CHATS },
      { label: "Контроль качества", icon: <ClipboardCheck size={14} />, to: "/qc", needAny: [QC_REMARK, KNOWLEDGE_EDIT] },
      { label: "Жалобы", icon: <MessageSquareWarning size={14} />, to: "/complaints" },
    ],
  },
  {
    group: "Сотрудники",
    items: [
      { label: "Активность", icon: <UsersRound size={14} />, to: "/activity" },
      { label: "Простои", icon: <Hourglass size={14} />, to: "/idle" },
      { label: "График", icon: <CalendarDays size={14} />, to: "/schedule" },
      { label: "Премия", icon: <Coins size={14} />, to: "/bonus" },
      { label: "ЗП", icon: <Wallet size={14} />, to: "/salary" },
    ],
  },
  {
    group: "Салоны",
    items: [
      { label: "Смена салона", icon: <Store size={14} />, to: "/salon" },
      DELIVERY,
      { label: "Невозвраты", icon: <PackageX size={14} />, to: "/nonreturn" },
      { label: "Видео", icon: <Video size={14} />, to: "/video", need: VIDEO_VIEW },
    ],
  },
  {
    group: "Маркетинг",
    items: [
      { label: "Рассылки", icon: <Megaphone size={14} />, to: "/mailing" },
      { label: "Личный кабинет", icon: <UserRound size={14} />, to: "/cabinet" },
      { label: "Сайт", icon: <ChartLine size={14} />, to: "/site" },
      { label: "Анализ", icon: <FileChartColumn size={14} />, to: "/analysis" },
    ],
  },
  {
    // Голосовой робот и всё, что нужно для прозвона им (27.10.26).
    group: "AI-обзвон",
    items: [
      { label: "Голосовой робот", icon: <AudioLines size={14} />, to: "/voice-lab", need: VOICE_LAB },
      { label: "Журнал звонков робота", icon: <ScrollText size={14} />, to: "/voice-lab?tab=calls", need: VOICE_LAB },
      { label: "Задачи робота", icon: <ListTodo size={14} />, to: "/voice-lab?tab=problems", need: VOICE_LAB },
      { label: "Телефония", icon: <Headset size={14} />, to: "/telephony" },
    ],
  },
  {
    group: "Настройки",
    items: [
      { label: "Сотрудники ↔ Bitrix", icon: <Link2 size={14} />, to: "/bitrix-users" },
      { label: "Telegram", icon: <Send size={14} />, to: "/telegram" },
      { label: "Боты", icon: <Bot size={14} />, to: "/bots", need: CHAT_GROUPS },
      { label: "API", icon: <Activity size={14} />, to: "/integrations" },
      { label: "Расширение Chrome", icon: <Puzzle size={14} />, to: "/extension" },
      { label: "Полномочия", icon: <KeyRound size={14} />, to: "/settings/permissions", need: PERMISSIONS_EDIT },
    ],
  },
];

/** Каркас кабинета: сайдбар, шапка, рабочая область. Порт из supply. */
export function AppLayout({ children }: { children: ReactNode }) {
  const { data: user, isPending } = useGetIdentity<CurrentUser>();
  const { mutate: logout } = useLogout();
  const { pathname, search } = useLocation();
  // Пункт без права не рисуется, группа без пунктов — тоже.
  const nav = NAV.map((g) => ({ ...g, items: g.items.filter((i) => (!i.need || has(user, i.need)) && (!i.needAny || i.needAny.some((c) => has(user, c)))) })).filter(
    (g) => g.items.length > 0,
  );
  // Вложенный экран (`/activity/1193`) принадлежит своему разделу; пункт с `?tab=` — своей вкладке,
  // и он сильнее пункта того же экрана без вкладки.
  const tabOf = (q: string) => new URLSearchParams(q).get("tab");
  const matches = (to: string) => {
    const [path = to, q] = to.split("?");
    if (q !== undefined) return path === pathname && tabOf(q) === tabOf(search);
    return path === pathname || (path !== "/" && pathname.startsWith(`${path}/`));
  };
  const items = NAV.flatMap((g) => g.items);
  const current = items.find((i) => i.to.includes("?") && matches(i.to)) ?? items.find((i) => matches(i.to));
  const isActive = (to: string) => current?.to === to;
  // Открыт один блок. Экран, которого не видно в открытом блоке (пришли строкой навигации,
  // ссылкой), раскрывает свой: сначала «Важное», если он там есть.
  const curTo = current?.to;
  const groupOf = (to?: string) => nav.find((g) => g.items.some((i) => i.to === to))?.group ?? null;
  const [open, setOpen] = useState<string | null>(() => groupOf(curTo) ?? NAV[0]!.group);
  const [seenTo, setSeenTo] = useState(curTo);
  if (seenTo !== curTo) {
    setSeenTo(curTo);
    if (curTo && !nav.find((g) => g.group === open)?.items.some((i) => i.to === curTo)) setOpen(groupOf(curTo));
  }
  const onDeal = pathname === "/deals" || pathname.startsWith("/deals/");
  const [theme, setTheme] = useState<"dark" | "light">(
    () => (document.documentElement.dataset["theme"] as "dark" | "light") ?? "dark",
  );

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    document.documentElement.dataset["theme"] = next;
    setTheme(next);
  };

  return (
    <div className="grid h-screen grid-cols-[var(--spacing-sidebar)_minmax(0,1fr)] overflow-hidden">
      <aside className="flex h-screen flex-col border-r border-border bg-bg-subtle">
        <div className="flex h-[var(--spacing-topbar)] flex-none items-center gap-2.5 border-b border-border px-4">
          <div className="grid size-[22px] place-items-center rounded-[5px] bg-fg text-[12px] font-semibold text-bg">
            P
          </div>
          {/* Вместо надписи «prokat» — строка навигации, как в supply (20.10.26). */}
          <NavCommand />
          {/* Контур ЧИСЕЛ: какую CRM читает кабинет. */}
          <span
            title={user?.crm ? `кабинет читает CRM: ${user.crm}` : "контур CRM неизвестен"}
            className={`flex-none rounded border px-1.5 font-mono text-[10px] ${
              user?.crm_is_prod === false ? "border-warn text-warn" : "border-border text-fg-faint"
            }`}
          >
            {user?.crm_is_prod === false ? "dev" : "prod"}
          </span>
        </div>

        <nav className="flex-1 overflow-y-auto p-2">
          {nav.map((group) => {
            const isOpen = open === group.group;
            return (
            <div key={group.group} className="mb-1">
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : group.group)}
                aria-expanded={isOpen}
                className={`flex h-7 w-full items-center gap-1.5 rounded-[var(--radius-ui)] px-2 text-[10.5px] font-medium uppercase tracking-[0.06em] hover:bg-bg-hover hover:text-fg ${
                  isOpen ? "text-fg-muted" : "text-fg-faint"
                }`}
              >
                <ChevronRight size={12} className={`flex-none transition-transform ${isOpen ? "rotate-90" : ""}`} />
                {group.group}
                {!isOpen && <span className="ml-auto font-mono normal-case tracking-normal">{group.items.length}</span>}
              </button>
              {isOpen && <div className="pb-2">{group.items.map((item) => {
                const active = isActive(item.to);
                const cls =
                  "flex h-8 w-full items-center gap-2.5 rounded-[var(--radius-ui)] px-2 text-[13.5px] transition-colors " +
                  (active ? "bg-bg-hover font-medium text-fg" : "text-fg-muted hover:bg-bg-hover hover:text-fg");
                return (
                  <Link key={item.label} to={item.to} aria-current={active ? "page" : undefined} className={cls}>
                    {item.icon}
                    {item.label}
                  </Link>
                );
              })}</div>}
            </div>
            );
          })}
        </nav>

        <div className="grid flex-none gap-2 border-t border-border p-2">
          <div className="flex items-center gap-2 rounded-[var(--radius-ui)] px-1.5 py-1">
            <div className="size-[22px] flex-none rounded-full bg-accent" />
            <div className="min-w-0">
              <div className="truncate text-[12.5px]">{user?.name ?? "…"}</div>
              <div className="text-[11px] text-fg-faint">прокат</div>
            </div>
            <button
              type="button"
              onClick={() => logout()}
              title="Выйти"
              className="ml-auto rounded p-1 text-fg-faint hover:bg-bg-hover hover:text-fg"
            >
              <LogOut size={13} />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex min-h-0 min-w-0 flex-col">
        <header className="sticky top-0 z-30 flex h-[var(--spacing-topbar)] items-center gap-3 border-b border-border bg-bg/85 px-5 backdrop-blur">
          <div className="flex min-w-0 items-center gap-2 text-[13.5px] text-fg-muted">
            <span>uni-renter</span>
            <ChevronRight size={13} className="text-border-strong" />
            <span>prokat</span>
            <ChevronRight size={13} className="text-border-strong" />
            {/* На карточке сделки заголовок — сам номер: его можно сразу перебить. */}
            {onDeal ? <QuickJump /> : <span className="font-medium text-fg">{current?.label ?? "Главная"}</span>}
          </div>
          <div className="ml-auto flex items-center gap-2">
            {!onDeal && <QuickJump />}
            <button
              type="button"
              onClick={toggleTheme}
              title="Тема"
              className="grid h-8 w-8 place-items-center rounded-[var(--radius-ui)] border border-border hover:bg-bg-hover"
            >
              <Moon size={14} />
            </button>
          </div>
        </header>

        {/* Экран — после личности: права едут в ней. */}
        {user ? (
          children
        ) : (
          <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 text-[12.5px] text-fg-muted">
            {isPending ? "…" : "Не удалось узнать, кто вошёл, — обновите страницу."}
          </main>
        )}
      </div>
      <Annotator />
    </div>
  );
}
